'use client';

import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { CreateSqlEngineInputWorkerSizeEnum, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { WORKER_SIZE_LABELS } from '../../workerSizeLabels';
import type { CreateSqlEngineWizardState } from '../createSqlEngineWizardState';

const WORKER_SIZES: {
  value: CreateSqlEngineInputWorkerSizeEnum;
  label: string;
}[] = Object.values(CreateSqlEngineInputWorkerSizeEnum).map((value) => ({
  value,
  label: WORKER_SIZE_LABELS[value] ?? value,
}));

const roleArnConfig = getCloudFieldConfig(Provider.Aws, 'roleArn', {
  required: true,
});

export const CreateSqlEngineStep = (
  props: StepProps<CreateSqlEngineWizardState>
) => {
  const dal = useDAL();
  const { wizardState: state, setWizardState } = props;
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (partial: Partial<CreateSqlEngineWizardState>) =>
    setWizardState({ ...state, ...partial });

  const isAwsWorkspace = state.workspaceCloud.toLowerCase() === 'aws';

  const canCreate =
    state.name.trim().length >= 3 &&
    (!isAwsWorkspace ||
      roleArnConfig.validation.validateInput(state.iamRoleArn)) &&
    !creating;

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    try {
      await dal.lakehouse.createSqlEngine(state.workspaceId, {
        name: state.name.trim(),
        workerCount: state.workerCount,
        workerSize: state.workerSize,
        autoscaling: state.autoscaling,
        minWorkers: state.minWorkers,
        maxWorkers: state.maxWorkers,
        idleTimeoutMinutes: state.idleTimeoutEnabled
          ? state.idleTimeoutMinutes
          : null,
        ...(isAwsWorkspace && {
          awsRuntimeConfig: { iamRoleArn: state.iamRoleArn.trim() },
        }),
      });
      void props.finish('SQL engine created successfully');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create SQL engine'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <StepContainer
      hideStepper
      stepperIndex={0}
      canGoNext={() => canCreate}
      nextButtonText={creating ? 'Creating...' : 'Create'}
      nextButtonLoadingText='Creating...'
      backButtonText='Cancel'
      onBackClick={props.back}
      onNextClick={handleCreate}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Details */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Typography variant='h6'>Details</Typography>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>Name</Typography>
              </FormLabel>
              <TextField
                size='small'
                placeholder='Enter engine name'
                value={state.name}
                onChange={(e) => update({ name: e.target.value })}
                fullWidth
              />
            </FormControl>
          </Stack>
        </Box>

        {/* Worker Configuration */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Worker Configuration</Typography>
              <Typography variant='body2' color='text.secondary'>
                Choose the worker size and scaling behavior for the engine.
              </Typography>
            </Stack>

            <FormControl fullWidth size='small'>
              <FormLabel>
                <Typography variant='subtitle1'>Worker Size</Typography>
              </FormLabel>
              <Select
                size='small'
                value={state.workerSize}
                onChange={(e) => {
                  const size = WORKER_SIZES.find(
                    (s) => s.value === e.target.value
                  );
                  if (size) update({ workerSize: size.value });
                }}
              >
                {WORKER_SIZES.map((size) => (
                  <MenuItem key={size.value} value={size.value}>
                    {size.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={state.autoscaling}>
              <FormLabel>
                <Typography variant='subtitle1' color='inherit'>
                  Worker Count
                </Typography>
              </FormLabel>
              <TextField
                size='small'
                type='number'
                disabled={state.autoscaling}
                value={state.autoscaling ? '' : state.workerCount}
                onChange={(e) =>
                  update({
                    workerCount: Math.max(1, parseInt(e.target.value) || 1),
                  })
                }
                slotProps={{ htmlInput: { min: 1 } }}
                fullWidth
              />
            </FormControl>

            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Stack gap='4px'>
                <Typography variant='subtitle2' fontWeight={700}>
                  Autoscaling
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Automatically adjust the number of workers based on workload
                  demand.
                </Typography>
              </Stack>
              <Switch
                checked={state.autoscaling}
                onChange={(e) => update({ autoscaling: e.target.checked })}
              />
            </Stack>

            <Stack direction='row' gap='16px'>
              <FormControl fullWidth disabled={!state.autoscaling}>
                <FormLabel>
                  <Typography variant='subtitle1' color='inherit'>
                    Min Workers
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  disabled={!state.autoscaling}
                  value={state.autoscaling ? state.minWorkers : ''}
                  onChange={(e) =>
                    update({
                      minWorkers: Math.max(1, parseInt(e.target.value) || 1),
                    })
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>
              <FormControl fullWidth disabled={!state.autoscaling}>
                <FormLabel>
                  <Typography variant='subtitle1' color='inherit'>
                    Max Workers
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  disabled={!state.autoscaling}
                  value={state.autoscaling ? state.maxWorkers : ''}
                  onChange={(e) =>
                    update({
                      maxWorkers: Math.max(
                        state.minWorkers,
                        parseInt(e.target.value) || 1
                      ),
                    })
                  }
                  slotProps={{ htmlInput: { min: state.minWorkers } }}
                  fullWidth
                />
              </FormControl>
            </Stack>
          </Stack>
        </Box>

        {/* IAM Role (AWS only) */}
        {isAwsWorkspace && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>IAM Role</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Specify an IAM role for the engine to use at runtime.
                </Typography>
              </Stack>

              <FormTextField
                size='small'
                title='IAM Role ARN'
                placeholder={roleArnConfig.placeholder}
                validation={roleArnConfig.validation}
                value={state.iamRoleArn}
                onChange={(e) => update({ iamRoleArn: e.target.value })}
              />
            </Stack>
          </Box>
        )}

        {/* Idle Timeout */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Stack gap='8px'>
                <Typography variant='h6'>Idle Timeout</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Automatically stop the engine after a period of inactivity to
                  save resources.
                </Typography>
              </Stack>
              <Switch
                checked={state.idleTimeoutEnabled}
                onChange={(e) =>
                  update({ idleTimeoutEnabled: e.target.checked })
                }
              />
            </Stack>

            <FormControl fullWidth disabled={!state.idleTimeoutEnabled}>
              <FormLabel>
                <Typography variant='subtitle1' color='inherit'>
                  Timeout (minutes)
                </Typography>
              </FormLabel>
              <TextField
                size='small'
                type='number'
                disabled={!state.idleTimeoutEnabled}
                value={state.idleTimeoutEnabled ? state.idleTimeoutMinutes : ''}
                onChange={(e) =>
                  update({
                    idleTimeoutMinutes: Math.max(
                      1,
                      parseInt(e.target.value) || 1
                    ),
                  })
                }
                slotProps={{ htmlInput: { min: 1 } }}
                fullWidth
              />
            </FormControl>
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
